import React, { useEffect, useRef } from 'react';
import { useState } from 'react';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import { ButtonToolbar, MenuItem, Modal, Panel, SplitButton } from 'react-bootstrap';


import '../../Assets/Pages/conversionTunnel.css';

import { fetchApi } from '../../Network/NetworkService';
import { notif } from '../Ui/Notification';
import { CardCount } from '../Ui/Card';
import toYMD from '../../Services/toYMD';

// An option marked `ga: false` is a real value in the DATABASE (`utm_source`,
// `ab_variant`) but one GA4 has never recorded a session under, so it can filter
// the A/B comparison block below and not the GA4 funnel. Selecting one used to
// blank every card on the page with no explanation; now the page says so.
//
// Measured over 90 days: google 249 967 sessions, bing 1 855, amezian 1 182,
// skycall 491, feeli-blog 154 — and gescall, infirmiers, skycall-voice and
// pharmacy exactly 0. `chatgpt` is 0 under that name only because GA4 files the
// traffic under the referral host, hence `gaValue`.
const inputOptions = {
    // The list is replaced from /funnelTypes (37 live funnels, ordered by
    // traffic), but these two stay as the floor: a single failed GA4 call must
    // not leave a dropdown holding nothing but its own placeholder, with the
    // funnel unreachable. They are the two highest-traffic types by a wide
    // margin and were the only options this page ever had.
    requestState: [
        { value: null, label: 'Type' },
        { value: 'dermatologie', label: 'Dermatologie' },
        { value: 'generaliste', label: 'Generaliste' },
    ],
    sourceState: [
        { value: null, label: 'No source' },
        { value: 'google', label: 'Google' },
        { value: 'bing', label: 'Bing' },
        { value: 'feeli-blog', label: 'Blog Feeli' },
        { value: 'skycall', label: 'Skycall (tous)' },
        { value: 'gescall', label: 'Gescall (base uniquement)', ga: false },
        { value: 'infirmiers', label: 'Infirmiers (base uniquement)', ga: false },
        { value: 'amezian', label: 'Amezian' },
        { value: 'chatgpt', label: 'Chatgpt', gaValue: 'chatgpt.com' },
        { value: 'skycall-voice', label: 'Skycall Voice (base uniquement)', ga: false },
        { value: 'pharmacy', label: 'Pharmacie/Camping (base uniquement)', ga: false },

    ],
    device: [
        { value: null, label: 'Device category' },
        { value: 'desktop', label: 'Desktop' },
        { value: 'tablet', label: 'Tablet' },
        { value: 'mobile', label: 'Mobile' },
    ],
    abVariant: [
        { value: null, label: 'A/B Variant (tous)' },
        // feeli-client hardcodes ab_variant to 'v2' — in the gtag config of
        // pages/_document.js and in src/Constants.jsx — so GA4 holds 824 795 v2
        // pageviews and 0 v1 over 30 days. The database still resolves v1 (it is
        // the fallback when a request carries no variant), so the option stays
        // for the comparison block.
        { value: 'v1', label: 'V1 (base uniquement)', ga: false },
        { value: 'v2', label: 'V2' },
    ],
};

// Human label for a questionnaire slug coming back from /funnelTypes.
const TYPE_LABELS = {
    generaliste: 'Generaliste',
    dermatologie: 'Dermatologie',
};

const typeLabel = (slug) => TYPE_LABELS[slug]
    || slug.replace(/-/g, ' ').replace(/^./, (c) => c.toUpperCase());

const pct = (value, base) => (base > 0 ? ((value / base) * 100).toFixed(1) : null);


const ConversionTunnel = () => {
    const oneMonthBefore = new Date();

    oneMonthBefore.setMonth(oneMonthBefore.getMonth() - 1);

    const [withTypeData, setWithTypeData] =  useState(null);
    const [pageView, setPageView] = useState(0);
    const [questions, setQuestions] = useState(null);
    const [typeOptions, setTypeOptions] = useState(inputOptions.requestState);
    const [variantStats, setVariantStats] = useState([]);
    const [googleAds, setGoogleAds] = useState({ campaigns: [], totals: null });
    const [googleRequests, setGoogleRequests] = useState({ revenue: 0, count: 0 });
    const [dateValue, setDateValue] = useState([
        oneMonthBefore.toISOString().split('T')[0],
        new Date().toISOString().split('T')[0],
    ])
    const [selectValue, setSelectValue] = useState({
        device: inputOptions.device[0],
        requestState: inputOptions.requestState[0],
        sourceState: inputOptions.sourceState[0],
        abVariant: inputOptions.abVariant[0]
    })
    // Counter, not a boolean: four requests are fired per refresh and only two of
    // them used to clear the flag, so the first answer hid the spinner while the
    // rest of the page was still empty.
    const [pending, setPending] = useState(0);
    const loading = pending > 0;

    // Monotonic id of the newest selection. Clearing nothing stops a request
    // already in flight, so each fetch below checks it is still the current one
    // before writing state — otherwise a slow earlier reply lands last and wins.
    const latestRequest = useRef(0);

    const track = (promise) => {
        setPending((n) => n + 1);
        return promise.then(
            (v) => { setPending((n) => n - 1); return v; },
            (e) => { setPending((n) => n - 1); return Promise.reject(e); }
        );
    };

    // Every GA4 call must receive the same calendar days the Google Ads block
    // gets. Posting the raw picker value sent a local-midnight Date, which
    // serialises to the previous day in UTC (…T22:00:00.000Z in Paris) and the
    // server reads the UTC day — so the funnel silently described a window one
    // day behind the Ads panel sitting next to it.
    const gaDateValue = [toYMD(dateValue[0]), toYMD(dateValue[1])];

    // Filters the GA4 funnel cannot honour (see `ga: false` above).
    const gaBlockedBy = [selectValue.sourceState, selectValue.abVariant]
        .filter((o) => o && o.value && o.ga === false);
    const gaBlocked = gaBlockedBy.length > 0;

    // What the server should filter GA4 on, which is not always what the
    // database is filtered on.
    const gaSelectValue = () => ({
        device: selectValue.device,
        requestState: selectValue.requestState,
        abVariant: selectValue.abVariant,
        sourceState: selectValue.sourceState && selectValue.sourceState.gaValue
            ? { ...selectValue.sourceState, value: selectValue.sourceState.gaValue }
            : selectValue.sourceState
    });

    const getFunnelTypes = () => {
        fetchApi('/analyticData/funnelTypes')
            .then((res) => {
                const types = (res.data && res.data.types) || [];
                setTypeOptions([
                    inputOptions.requestState[0],
                    ...types.map((t) => ({
                        value: t.value,
                        label: `${typeLabel(t.value)} (${t.sessions.toLocaleString('fr-FR')})`
                    }))
                ]);
            })
            .catch(() => {
                // Keep the floor options rather than emptying the dropdown, and
                // say so: silently serving two types out of 37 looks like the
                // old hardcoded list, which is indistinguishable from working.
                notif.addNotification(
                    'Types de tunnel',
                    'Liste des types indisponible, seuls Dermatologie et Generaliste sont proposes.',
                    'warning'
                );
            });
    };

    const getHomePageCount = (fresh) => {
        return track(fetchApi(`/analyticData/getHomePageView`, {
            method: 'POST',
            body: JSON.stringify({
                dateValue: gaDateValue,
                selectValue: gaSelectValue()
            })
        }))
        .then((res) => {
            if (!fresh()) return;
            // `sessions`, not `pageview`: this sits next to a funnel measured in
            // sessions, and the two are not the same unit.
            setPageView((res.data && res.data.sessions) || 0);
        })
        .catch(() => {
            if (!fresh()) return;
            notif.addNotification(
                'Erreur',
                'Une erreur est survenue !',
                'warning'
            )
        });
    }

    const getPerTypeCount = (fresh) => {
        return track(fetchApi(`/analyticData/getPerTypeCount`, {
            method: 'POST',
            body: JSON.stringify({
                dateValue: gaDateValue,
                selectValue: gaSelectValue()
            })
        }))
        .then((res) => {
            if (!fresh()) return;
            if (res && res.data) {
                setWithTypeData(res.data);
            } else {
                setWithTypeData(null);
            }
        })
        .catch(() => {
            if (!fresh()) return;
            notif.addNotification(
                'Erreur',
                'Une erreur est survenue !',
                'warning'
            )
            setWithTypeData(null);
        });
    }

    // Question wording comes from the questionnaire itself (api.feeli.io
    // /pathology/:name, proxied by the admin). It used to be a map hardcoded in
    // this file: a question added at the head of `generaliste` shifted every
    // label from question 2 on, and left question 11 with an empty red box.
    const getQuestions = (type, fresh) => {
        return track(fetchApi(`/analyticData/questionnaire/${encodeURIComponent(type)}`))
            .then((res) => { if (fresh()) setQuestions((res && res.data) || null); })
            .catch(() => { if (fresh()) setQuestions(null); });
    };

    const getGoogleAds = (fresh) => {
        return track(fetchApi(`/analyticData/getGoogleAdAnalytics`, {
            method: 'POST',
            body: JSON.stringify({ dateValue: gaDateValue })
        }))
        .then(res => { if (fresh()) setGoogleAds(res.data || { campaigns: [], totals: null }); })
        .catch(() => { if (fresh()) setGoogleAds({ campaigns: [], totals: null }); });
    };

    const getGoogleRequests = (fresh) => {
        // Align to the same calendar days GA4 uses (UTC midnight bounds).
        const start = `${gaDateValue[0]}T00:00:00.000Z`;
        const end = `${gaDateValue[1]}T23:59:59.999Z`;
        return track(fetchApi(`/requests/analytics/aggregated?start=${start}&end=${end}`))
            .then(res => {
                if (!fresh()) return;
                const g = (res.data && res.data.bySource && res.data.bySource.google) || {};
                setGoogleRequests({ revenue: g.revenue || 0, count: g.count || 0 });
            })
            .catch(() => { if (fresh()) setGoogleRequests({ revenue: 0, count: 0 }); });
    };

    const getVariantStats = (fresh) => {
        return track(fetchApi(`/analyticData/getConversionByVariant`, {
            method: 'POST',
            body: JSON.stringify({
                dateValue: gaDateValue,
                selectValue
            })
        }))
        .then((res) => {
            if (!fresh()) return;
            if (res && res.data) {
                setVariantStats(res.data);
            } else {
                setVariantStats([]);
            }
        })
        .catch(() => {
            if (fresh()) setVariantStats([]);
        });
    }

    useEffect(() => {
        getFunnelTypes();
    }, []);

    useEffect(() => {
        setGoogleAds({ campaigns: [], totals: null });
        setGoogleRequests({ revenue: 0, count: 0 });

        // A later selection must win even if an earlier, slower one answers
        // after it. Switching from `generaliste` (a big, slow GA4 report) to
        // `acne` otherwise rendered acne first and then had generaliste's reply
        // overwrite it, leaving the funnel and the dropdown disagreeing with the
        // spinner already gone.
        const request = ++latestRequest.current;
        const fresh = () => request === latestRequest.current;

        // The DB-backed block answers every filter combination.
        getVariantStats(fresh);

        // Google Ads is filtered by date alone — getGoogleAdAnalytics reads only
        // dateValue, /requests/analytics/aggregated only start/end — so it is
        // not affected by a source or variant GA4 cannot honour, and blanking it
        // there would hide a working panel for no reason.
        getGoogleAds(fresh);
        getGoogleRequests(fresh);

        if (gaBlocked) {
            // Don't spend GA4 calls on a filter it has no rows for, and don't
            // leave the previous selection's numbers on screen pretending to be
            // this one's.
            setPageView(0);
            setWithTypeData(null);
            setQuestions(null);
            return;
        }

        getHomePageCount(fresh);

        if (selectValue.requestState.value) {
            getPerTypeCount(fresh);
            getQuestions(selectValue.requestState.value, fresh);
        } else {
            // Was never cleared: going back to "Type" left the previous type's
            // funnel on screen as though it were the current selection.
            setWithTypeData(null);
            setQuestions(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectValue, dateValue])


    const handleSelect = (e, name) => {
        setSelectValue({ ...selectValue, [name]: e });
    }

    const optionsFor = (key) => (key === 'requestState' ? typeOptions : inputOptions[key]);

    const renderSplitButton = () => {
        return Object.keys(inputOptions).map((v) => (
            <ButtonToolbar key={v} width='200px'>
                <SplitButton
                    id="option-list"
                    rootCloseEvent="mousedown"
                    title={selectValue[v].label}
                >   
                    {
                        optionsFor(v).map((k) => (
                            <MenuItem
                                eventKey={k}
                                active={k.value === selectValue[v].value}
                                onSelect={k.value === selectValue[v].value ? undefined : (e) => handleSelect(e, v)}
                                key={k.label}
                            >
                                {k.label}
                            </MenuItem>
                        ))
                    }
                </SplitButton>
            </ButtonToolbar>
        ))
    }

    const renderQuestText = (questionNumber) => {
        if (!questions || !questions.questions) return null;
        const q = questions.questions[questionNumber - 1];
        if (!q || !q.label) return null;

        return (
            <div style={{ textAlign: 'center', padding: 10, border: '1px solid red', fontSize: 20, width: 430 }}>
                <p style={{ margin: 0 }}>{q.label}</p>
            </div>
        );
    }

    const renderQuestImg = (name, path) => {
        if (name.includes('Etape 2')) {
            return (
                <div style={{ width: 430 }}>
                    <a href={`https://admin.feeli.io/assets/images/e2.png`} target='_blank' rel="noreferrer">
                        <img height={100} src="/assets/images/e2.png" alt="step2img" />
                    </a>
                </div>
            )
        }

        if (name.includes('Etape 3')) {
            let src;

            if (path.includes('dermatologie')) {
                src = "/assets/images/d-e3.png";
            } 
            else if (path.includes('generaliste')) {
                src = "/assets/images/g-e3.png";
            }

            if (!src) return null;

            return (
                <div style={{ width: 430 }}>
                    <a href={`https://admin.feeli.io${src}`} target='_blank' rel="noreferrer">
                        <img height={100}  src={src} alt="step3img" />
                    </a>
                </div>
            )
        }
        return null;
    }
    

    const renderCardData = () => {
        if (!withTypeData || !withTypeData.index) {
            return null;
        }

        const path = withTypeData.index.path || '';
        const entry = withTypeData.entry || { path: '', count: 0, views: 0 };

        // The funnel is measured against the questionnaires actually OPENED, not
        // against homepage views. Most of this traffic arrives on /symptome/*
        // straight from Ads or SEO and never sees the homepage, which is why the
        // first question used to read 157% "du total".
        //
        // The homepage count is deliberately NOT a fallback here. Falling back to
        // it would divide sessions by homepage traffic for exactly the types that
        // have no entry and no landing rows — reintroducing the 157% arithmetic
        // through the back door, under a caption promising otherwise. With no
        // denominator the percentages read "—".
        const base = entry.count || withTypeData.index.count || 0;

        const chain = [
            {
                key: 'landing',
                name: `Page ${path}`,
                value: withTypeData.index.count,
                views: withTypeData.index.views,
                background: '#00cdac'
            },
            {
                key: 'entry',
                name: 'Questionnaire ouvert',
                // The path goes on a second line, not in the caption: captioned
                // with the raw URL, the one card the whole funnel is divided by
                // never actually said what it was, while the banner underneath
                // talked about "questionnaires ouverts".
                detail: entry.path,
                value: entry.count,
                views: entry.views,
                background: '#00cdac'
            },
            ...(withTypeData.quest || []).map((v) => {
                const q = v.name.match(/^Etape 1 - Question (\d+)$/);

                return {
                    key: v.name,
                    name: v.name,
                    value: v.value,
                    views: v.views,
                    questionNumber: q ? parseInt(q[1], 10) : null,
                    background: 'rgb(33, 134, 51)'
                };
            })
        ];

        // The questionnaire grew or shrank upstream and the funnel is reading a
        // different number of questions than the live questionnaire has. Said out
        // loud, because the previous silent version of this mismatch is what made
        // every `generaliste` label wrong for months.
        // `>` comparisons, not `&&` over numbers: `questions.count && questionSteps
        // && ...` evaluates to the NUMBER 0 when either is 0, and `{0 && ...}`
        // renders a bare "0" into the page where the warning belongs. That is
        // reachable: a type whose GA4 rows in this range hold no /etape1/<i>-<N>
        // path gives questionSteps === 0.
        const questionSteps = chain.filter((c) => c.questionNumber).length;
        const drift = Boolean(questions)
            && questions.count > 0
            && questionSteps > 0
            && questions.count !== questionSteps;

        return (
            <div className='card-block'>
                <p style={{ color: '#666', fontSize: 13, marginBottom: 12 }}>
                    Étapes mesurées en <strong>sessions</strong> ({withTypeData.metric}).
                    Les pourcentages sont rapportés aux <strong>questionnaires ouverts</strong>
                    {base > 0 ? ` (${base.toLocaleString('fr-FR')})` : ''}.
                </p>

                {drift && (
                    <p style={{ color: '#b8860b', fontSize: 13, marginBottom: 12 }}>
                        Le questionnaire en ligne compte {questions.count} questions, le tunnel en mesure {questionSteps} :
                        les libellés peuvent être décalés le temps que GA4 enregistre les nouvelles URLs.
                    </p>
                )}

                {chain.map((v, index) => {
                    const previous = index > 0 ? chain[index - 1] : null;
                    const percentage = pct(v.value, base);
                    const delta = previous && previous.value > 0
                        ? ((v.value / previous.value) - 1) * 100
                        : null;

                    return (
                        <div key={v.key}>
                            <div className='type-block'>
                                <div style={{ textAlign: 'center' }}>
                                    {index > 0 && (
                                        <i style={{ fontSize: '40px', color: '#00ffeb', marginBottom: 10 }} className="zmdi zmdi-long-arrow-down"></i>
                                    )}
                                    {delta !== null && delta < 0 && (
                                        <div style={{
                                            color: delta < -30 ? '#ff4444' : '#ffaa00',
                                            fontSize: '12px',
                                            marginBottom: '5px',
                                            fontWeight: 'bold'
                                        }}>
                                            -{Math.abs(delta).toFixed(1)}% abandonnent
                                        </div>
                                    )}
                                    {/* A step can legitimately GROW: /etape2 is reachable
                                        by deep link (photo-reminder mails land there with
                                        ?targetStep=files) without passing through etape1.
                                        Hiding a positive delta hid that, and hid a funnel
                                        that was not monotonic at all. */}
                                    {delta !== null && delta > 0 && (
                                        <div
                                            style={{ color: '#4a90e2', fontSize: '12px', marginBottom: '5px', fontWeight: 'bold' }}
                                            title="Étape atteinte sans passer par la précédente (lien direct, e-mail de rappel photo)"
                                        >
                                            +{delta.toFixed(1)}% entrées directes
                                        </div>
                                    )}
                                    <CardCount
                                        number={v.value.toLocaleString('fr-FR')}
                                        subtitle={v.name}
                                        icon="fa fa-eye"
                                        background={v.background}
                                    />
                                    {v.detail && (
                                        <div style={{ color: '#999', fontSize: '12px', marginTop: '4px' }}>
                                            {v.detail}
                                        </div>
                                    )}
                                    <div style={{
                                        color: '#666',
                                        fontSize: '14px',
                                        marginTop: '5px',
                                        fontWeight: 'bold'
                                    }}>
                                        {percentage === null ? '—' : `${percentage}% des questionnaires`}
                                    </div>
                                    <div style={{ color: '#999', fontSize: '12px', marginTop: '2px' }}>
                                        {v.views.toLocaleString('fr-FR')} pages vues
                                    </div>
                                </div>

                                {v.questionNumber && renderQuestText(v.questionNumber)}
                                {renderQuestImg(v.name, path)}
                                <div>

                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        )
    }

    return (
        <Panel>
            <Modal
                show={loading}
                backdrop="static"
                keyboard={false}
                size="lg"
                className="modal-spinner"
            >
                <span className="loader" />
                {loading && <p>Récupération des données en cours...</p>}
            </Modal>
            <Panel.Heading>
                <h6>Tunnel de conversion</h6>
            </Panel.Heading>
            <Panel.Body>
                <div className='conversion-tunnel'>
                    <div className='block-1'>
                        <div>
                         <DateRangePicker
                            locale="fr-FR"
                            onChange={value => {
                                setDateValue(value)
                            }}
                            value={dateValue}
                            maxDate={new Date()}
                            clearIcon={null}
                        />
                        </div>
                        <div className='select-block'>
                            {renderSplitButton()}
                        </div>

                    </div>

                    <div className='block-2'>
                        <CardCount
                            number={pageView.toLocaleString('fr-FR')}
                            subtitle="Page d'acceuil — sessions (contexte)"
                            icon="fa fa-eye"
                            background={'#00cdac'}
                            width="300px"
                        />
                    </div>
                </div>

                {gaBlocked && (
                    <div style={{
                        marginTop: 20,
                        padding: '12px 16px',
                        borderLeft: '4px solid #ff9800',
                        backgroundColor: '#fff8e1',
                        fontSize: 13
                    }}>
                        <strong>Tunnel GA4 indisponible pour ce filtre.</strong>{' '}
                        {gaBlockedBy.map((o) => o.label).join(', ')} n'existe pas dans GA4 :
                        aucune session n'y a jamais été enregistrée sous ce nom. Le tunnel est
                        donc masqué plutôt qu'affiché à zéro. La comparaison A/B ci-dessous,
                        qui lit la base de données, reste valable.
                    </div>
                )}

                <div style={{ marginTop: '20px' }}>
                    {renderCardData()}
                </div>

                {/* A/B Variant Comparison Section */}
                {variantStats && variantStats.length > 0 && (
                    <div style={{ marginTop: '40px', borderTop: '2px solid #ddd', paddingTop: '20px' }}>
                        <h4 style={{ marginBottom: '20px' }}>Comparaison A/B Variants (Donnees Base de Donnees)</h4>

                        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '20px' }}>
                            {variantStats.map(stat => (
                                <div key={stat.variant} style={{
                                    border: '1px solid #ddd',
                                    borderRadius: '8px',
                                    padding: '20px',
                                    minWidth: '250px',
                                    backgroundColor: stat.variant === 'v1' ? '#e3f2fd' : '#e8f5e9'
                                }}>
                                    <h3 style={{
                                        color: stat.variant === 'v1' ? '#1976d2' : '#4caf50',
                                        marginBottom: '15px',
                                        textAlign: 'center'
                                    }}>
                                        {stat.variant.toUpperCase()}
                                    </h3>
                                    <table style={{ width: '100%', fontSize: '14px' }}>
                                        <tbody>
                                            <tr>
                                                <td>Total requetes:</td>
                                                <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{stat.total}</td>
                                            </tr>
                                            <tr>
                                                <td>Incompletes:</td>
                                                <td style={{ textAlign: 'right' }}>{stat.incomplete}</td>
                                            </tr>
                                            <tr>
                                                <td>Payees:</td>
                                                <td style={{ textAlign: 'right', color: '#4caf50', fontWeight: 'bold' }}>{stat.paid}</td>
                                            </tr>
                                            <tr>
                                                <td>En attente:</td>
                                                <td style={{ textAlign: 'right' }}>{stat.waiting}</td>
                                            </tr>
                                            <tr>
                                                <td>Approuvees:</td>
                                                <td style={{ textAlign: 'right' }}>{stat.approved}</td>
                                            </tr>
                                            <tr>
                                                <td>Terminees:</td>
                                                <td style={{ textAlign: 'right', color: '#1976d2', fontWeight: 'bold' }}>{stat.completed}</td>
                                            </tr>
                                            <tr>
                                                <td>Annulees/Remboursees:</td>
                                                <td style={{ textAlign: 'right', color: '#f44336' }}>{stat.canceled}</td>
                                            </tr>
                                            <tr style={{ borderTop: '1px solid #ddd' }}>
                                                <td style={{ paddingTop: '10px' }}>Taux completion:</td>
                                                <td style={{ textAlign: 'right', paddingTop: '10px', color: '#4caf50', fontWeight: 'bold', fontSize: '16px' }}>
                                                    {stat.completionRate}%
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>Taux annulation:</td>
                                                <td style={{ textAlign: 'right', color: '#ff9800', fontWeight: 'bold' }}>
                                                    {stat.cancelRate}%
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            ))}
                        </div>

                        {variantStats.length >= 2 && (
                            <div style={{
                                backgroundColor: '#f5f5f5',
                                padding: '15px',
                                borderRadius: '8px',
                                marginTop: '10px'
                            }}>
                                <h5>Comparaison V1 vs V2:</h5>
                                {(() => {
                                    const v1 = variantStats.find(s => s.variant === 'v1');
                                    const v2 = variantStats.find(s => s.variant === 'v2');
                                    if (!v1 || !v2) return null;
                                    const completionDiff = (v2.completionRate - v1.completionRate).toFixed(1);
                                    const cancelDiff = (v2.cancelRate - v1.cancelRate).toFixed(1);
                                    return (
                                        <div>
                                            <p style={{ margin: '5px 0' }}>
                                                Taux de completion: V2 est{' '}
                                                <strong style={{ color: completionDiff >= 0 ? '#4caf50' : '#f44336' }}>
                                                    {completionDiff >= 0 ? '+' : ''}{completionDiff}%
                                                </strong>
                                                {' '}par rapport a V1
                                            </p>
                                            <p style={{ margin: '5px 0' }}>
                                                Taux d'annulation: V2 est{' '}
                                                <strong style={{ color: cancelDiff <= 0 ? '#4caf50' : '#f44336' }}>
                                                    {cancelDiff >= 0 ? '+' : ''}{cancelDiff}%
                                                </strong>
                                                {' '}par rapport a V1
                                            </p>
                                        </div>
                                    );
                                })()}
                            </div>
                        )}
                    </div>
                )}

                {googleAds.totals && googleAds.totals.cost > 0 && (
                    <div style={{ marginTop: '40px', borderTop: '2px solid #ddd', paddingTop: '20px' }}>
                        <h4 style={{ marginBottom: '20px' }}>Performance Google Ads</h4>

                        {(() => {
                            const t = googleAds.totals;
                            const paidCount = googleRequests.count;
                            const paidRevenue = googleRequests.revenue;
                            const fmtEuro = n => `${Math.round(n).toLocaleString('fr-FR')} €`;
                            const safeDiv = (a, b) => b > 0 ? a / b : 0;
                            const ctr = safeDiv(t.clicks, t.impressions) * 100;
                            const cpc = safeDiv(t.cost, t.clicks);
                            const cpa = safeDiv(t.cost, paidCount);
                            const roas = safeDiv(paidRevenue, t.cost);
                            const conversionRate = safeDiv(paidCount, t.clicks) * 100;

                            const kpiStyle = (color) => ({
                                flex: '1 1 160px',
                                minWidth: 160,
                                padding: 16,
                                borderRadius: 8,
                                backgroundColor: '#fff',
                                border: `1px solid #e0e0e0`,
                                borderLeft: `4px solid ${color}`,
                            });

                            return (
                                <>
                                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                                        <div style={kpiStyle('#1976d2')}>
                                            <div style={{ color: '#666', fontSize: 12 }}>Dépense</div>
                                            <div style={{ fontSize: 24, fontWeight: 'bold' }}>{fmtEuro(t.cost)}</div>
                                        </div>
                                        <div style={kpiStyle('#1976d2')}>
                                            <div style={{ color: '#666', fontSize: 12 }}>Impressions</div>
                                            <div style={{ fontSize: 24, fontWeight: 'bold' }}>{t.impressions.toLocaleString('fr-FR')}</div>
                                        </div>
                                        <div style={kpiStyle('#1976d2')}>
                                            <div style={{ color: '#666', fontSize: 12 }}>Clics</div>
                                            <div style={{ fontSize: 24, fontWeight: 'bold' }}>{t.clicks.toLocaleString('fr-FR')}</div>
                                        </div>
                                        <div style={kpiStyle('#1976d2')}>
                                            <div style={{ color: '#666', fontSize: 12 }}>Sessions</div>
                                            <div style={{ fontSize: 24, fontWeight: 'bold' }}>{t.sessions.toLocaleString('fr-FR')}</div>
                                        </div>
                                        <div style={kpiStyle('#4caf50')}>
                                            <div style={{ color: '#666', fontSize: 12 }}>Clients acquis (base)</div>
                                            <div style={{ fontSize: 24, fontWeight: 'bold' }}>{paidCount.toLocaleString('fr-FR')}</div>
                                        </div>
                                        <div style={kpiStyle('#4caf50')}>
                                            <div style={{ color: '#666', fontSize: 12 }}>CA Google (base)</div>
                                            <div style={{ fontSize: 24, fontWeight: 'bold' }}>{fmtEuro(paidRevenue)}</div>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                                        <div style={kpiStyle('#ff9800')}>
                                            <div style={{ color: '#666', fontSize: 12 }}>CTR</div>
                                            <div style={{ fontSize: 24, fontWeight: 'bold' }}>{ctr.toFixed(2)}%</div>
                                        </div>
                                        <div style={kpiStyle('#ff9800')}>
                                            <div style={{ color: '#666', fontSize: 12 }}>CPC</div>
                                            <div style={{ fontSize: 24, fontWeight: 'bold' }}>{cpc.toFixed(2)} €</div>
                                        </div>
                                        <div style={kpiStyle('#ff9800')}>
                                            <div style={{ color: '#666', fontSize: 12 }}>Taux conversion</div>
                                            <div style={{ fontSize: 24, fontWeight: 'bold' }}>{conversionRate.toFixed(2)}%</div>
                                        </div>
                                        {/* Seuil CPA: prix moyen consultation ~30-45€, vert si rentable */}
                                        <div style={kpiStyle(cpa > 0 && cpa < 50 ? '#4caf50' : '#f44336')}>
                                            <div style={{ color: '#666', fontSize: 12 }}>CPA (coût / client payant)</div>
                                            <div style={{ fontSize: 24, fontWeight: 'bold' }}>{cpa.toFixed(2)} €</div>
                                        </div>
                                        <div style={kpiStyle(roas >= 1 ? '#4caf50' : '#f44336')}>
                                            <div style={{ color: '#666', fontSize: 12 }}>ROAS (CA / dépense)</div>
                                            <div style={{ fontSize: 24, fontWeight: 'bold' }}>{roas.toFixed(2)}x</div>
                                        </div>
                                    </div>
                                </>
                            );
                        })()}

                        {googleAds.campaigns && googleAds.campaigns.length > 0 && (
                            <div style={{ marginTop: 30 }}>
                                <h5 style={{ marginBottom: 10 }}>Par campagne</h5>
                                <div style={{ overflowX: 'auto' }}>
                                    <table className="table table-hover" style={{ fontSize: 13 }}>
                                        <thead>
                                            <tr>
                                                <th>Campagne</th>
                                                <th>Ad group</th>
                                                <th style={{ textAlign: 'right' }}>Impressions</th>
                                                <th style={{ textAlign: 'right' }}>Clics</th>
                                                <th style={{ textAlign: 'right' }}>Sessions</th>
                                                <th style={{ textAlign: 'right' }}>Dépense</th>
                                                <th style={{ textAlign: 'right' }}>CTR</th>
                                                <th style={{ textAlign: 'right' }}>CPC</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {googleAds.campaigns.map((c) => {
                                                const ctr = c.impressions > 0 ? (c.clicks / c.impressions) * 100 : 0;
                                                const cpc = c.clicks > 0 ? c.cost / c.clicks : 0;
                                                return (
                                                    <tr key={`${c.campaign}|${c.adGroup}`}>
                                                        <td>{c.campaign}</td>
                                                        <td>{c.adGroup}</td>
                                                        <td style={{ textAlign: 'right' }}>{c.impressions.toLocaleString('fr-FR')}</td>
                                                        <td style={{ textAlign: 'right' }}>{c.clicks.toLocaleString('fr-FR')}</td>
                                                        <td style={{ textAlign: 'right' }}>{c.sessions.toLocaleString('fr-FR')}</td>
                                                        <td style={{ textAlign: 'right' }}>{Math.round(c.cost).toLocaleString('fr-FR')} €</td>
                                                        <td style={{ textAlign: 'right' }}>{ctr.toFixed(2)}%</td>
                                                        <td style={{ textAlign: 'right' }}>{cpc.toFixed(2)} €</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                <p style={{ color: '#888', fontSize: 12, marginTop: 8 }}>
                                    * Clients acquis et CA par campagne non disponibles (utm_campaign non tracké sur les requests). CPA et ROAS sont calculés au niveau agrégé.
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </Panel.Body>
        </Panel>
    )
}

export default ConversionTunnel;